import { CellContext, Column, Row } from '@tanstack/react-table';
import { FC, useCallback, useEffect, useState } from 'react';
import { DisplayOccasionWithoutIcon } from '@/components/DisplayOccasionWithIcon/DisplayOccasionWithIcon';
import Header from '@/components/Header/Header';
import Link from '@/components/Link/Link';
import Table from '@/components/Tables/Table/Table';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import { usePromotionsData, usePromoLockUpdate } from '@/services/promotions/promotions.service';
import {
  filterBooleans,
  filterNumbers,
  filterPromotionOccasions,
  identicalArrays,
} from '@/services/promotions/promotions.helper.service';
import { PromoSelection, PromotionData } from '@/types/promotions.types';
import styles from './ActivePromotions.module.css';
import { useTranslation } from 'react-i18next';
import { Grid, Switch } from '@mui/material';
import Button from '@/components/Button/Button';
import { SwitchCell } from './SwitchCell';

const ViewDetails = ({ row }: CellContext<PromotionData, unknown>) => (
  <Link to={`/promotions/${row.original.promotionId}`}>
    <span className={styles.promotionName}>{row.original.name}</span>
  </Link>
);

const combinable = ({ row }: CellContext<PromotionData, unknown>) => {
  if (row.original.combinable) return 'Yes';
  if (!row.original.combinable) return 'No';
  return '';
};
const isPromoVisible = ({ row }: CellContext<PromotionData, unknown>) => {
  if (row.original.isVisible) return 'Yes';
  if (!row.original.isVisible) return 'No';
  return '';
};

const ActivePromotions: FC = () => {
  const { t } = useTranslation();
  const { data: promoData, isLoading, isError } = usePromotionsData(true, 'NON-CER');
  const { mutate } = usePromoLockUpdate();

  const [data, setData] = useState<PromotionData[]>(promoData?.data || []);
  const [originalData, setOriginalData] = useState<PromotionData[]>(promoData?.data || []);
  const [filteredData, setFilteredData] = useState<PromotionData[]>([]);
  const [disableSaveChangesButton, setDisableSaveChangesButton] = useState(true);
  const [registerCount, setRegisterCount] = useState(0);
  const [isAllLocked, setIsAllLocked] = useState(false);

  const isFiltering = data.length > filteredData.length;

  const handleAllLockPromotions = () => {
    if (isFiltering) {
      const updateData = filteredData.map((promo) => ({
        ...promo,
        isLock: !isAllLocked,
        isAllLock: !promo.isLock,
      }));
      setFilteredData(updateData);
      setIsAllLocked((prevState) => !prevState);
    } else {
      setFilteredData(data);
    }

    const updateData = data.map((promo) => {
      const isFiltered = filteredData.some((filteredPromo) => filteredPromo.promotionId === promo.promotionId);

      return isFiltered ? { ...promo, isLock: !isAllLocked, isAllLock: !promo.isLock } : promo;
    });
    setData(updateData);
    setIsAllLocked((prevState) => !prevState);
  };

  const columns: any[] = [
    {
      accessorKey: 'name',
      header: t('promotions.table.name'),
      enableSorting: true,
      cell: ViewDetails,
    },
    {
      accessorKey: 'activeStoreCount',
      header: t('promotions.table.activeStoreCount'),
      enableSorting: true,
      filterFn: (row: Row<PromotionData>, columnId: string, filterValue: any) => {
        const promotion = row.original;
        return filterNumbers(promotion.activeStoreCount, filterValue);
      },
    },
    {
      accessorKey: 'occasions',
      header: t('promotions.table.occasion'),
      enableSorting: true,
      cell: DisplayOccasionWithoutIcon,
      filterFn: (row: Row<PromotionData>, columnId: string, filterValue: any) => {
        const promotion = row.original;
        return filterPromotionOccasions(promotion, filterValue);
      },
    },
    {
      accessorKey: 'combinable',
      header: t('promotions.table.combinable'),
      enableSorting: true,
      cell: combinable,
      filterFn: (row: Row<PromotionData>, columnId: string, filterValue: boolean) => {
        const promotion = row.original;
        return filterBooleans(promotion.combinable, filterValue);
      },
    },
    {
      accessorKey: 'maxUses',
      header: t('promotions.table.maxUses'),
      enableSorting: true,
      filterFn: (row: Row<PromotionData>, columnId: string, filterValue: any) => {
        const promotion = row.original;
        return filterNumbers(promotion.maxUses, filterValue);
      },
    },
    {
      accessorKey: 'isVisible',
      header: t('promotions.table.visible'),
      enableSorting: true,
      cell: isPromoVisible,
      filterFn: (row: Row<PromotionData>, columnId: string, filterValue: boolean) => {
        const promotion = row.original;
        return filterBooleans(promotion.isVisible, filterValue);
      },
    },
    {
      accessorKey: 'isLock',
      header: (
        <Grid display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1}>
          <span className={styles.lockHeader}>{t('promotions.table.lock')}</span>
          <Switch
            data-testid="switch-lock-all"
            color="error"
            title="switch"
            checked={isAllLocked}
            onClick={handleAllLockPromotions}
          />
        </Grid>
      ),
      cell: SwitchCell,
      meta: { disableFilter: true, hideFilter: true },
    },
  ];

  const onSaveChanges = useCallback(() => {
    const promosToUpdate: PromoSelection[] = [];

    originalData.forEach((promo) => {
      const resultObject = data.filter((promoDetails) => promoDetails.promotionId === promo.promotionId)[0];

      if (resultObject.isLock !== promo.isLock) {
        const promoUpdate: PromoSelection = {
          promotionId: resultObject.promotionId,
          isLock: resultObject.isLock,
        };
        promosToUpdate.push(promoUpdate);
      }
    });

    mutate(promosToUpdate, {
      onSuccess: () => {
        showToast({
          id: 'updatePromoLocks',
          label: t('promotions.isLockUpdated'),
        });
      },
      onError: () => {
        showToast({
          id: 'errorUpdatingPromoLocks',
          label: t('promotions.updateError'),
          toastType: ToastTypes.Error,
        });
      },
    });
  }, [data, mutate, originalData, t]);

  useEffect(() => {
    if (promoData?.data) {
      setData(promoData.data);
      setOriginalData(promoData.data);
    }
  }, [promoData]);

  useEffect(() => {
    if (isError) {
      showToast({
        label: t('error.fetchingPromotions'),
        toastType: ToastTypes.Error,
        id: 'errorFetchingPromotions',
      });
    }
  }, [t, isError]);

  useEffect(() => {
    const identicalData = identicalArrays(originalData, data);
    const allBlocked = isFiltering ? filteredData.every((promo) => promo.isLock) : data.every((promo) => promo.isLock);
    setIsAllLocked(allBlocked);
    setDisableSaveChangesButton(identicalData);
  }, [data, filteredData, isFiltering, originalData]);

  return (
    <>
      <Header title={t('promotions.title')} breadcrumb={{ label: t('breadcrumb.backToHome'), to: '/' }} />
      <div className={styles.page}>
        <div className={styles.tableLabel}>
          {t('promotions.promotionsLibrary')}
          <Button disabled={disableSaveChangesButton} onClick={onSaveChanges}>
            {t('promotions.saveButton')}
          </Button>
        </div>
        <div className={styles.headerTableDivider}>
          <Grid
            container
            direction="row"
            justifyContent="right"
            sx={{
              marginBottom: '10px',
              fontSize: '14px',
              lineHeight: '25px',
              color: '#838383',
            }}
          >
            {t('promotions.promotionsLibraryCount', {
              count: registerCount,
            })}
          </Grid>
        </div>
        <div className={styles.tableContainer}>
          <Table
            width="calc(100vw - 325px)"
            height="calc(100vh - 170px)"
            isLoading={isLoading}
            data={data}
            columns={columns}
            setRegisterCount={setRegisterCount}
            setFilteredData={setFilteredData}
            meta={{
              columns,
              data,
              updateData: (rowIndex: number, columnId: string, value: string) => {
                setData((old) =>
                  old?.map((row, index) => {
                    if (index === rowIndex) {
                      return {
                        ...old[rowIndex],
                        [columnId]: JSON.parse(value),
                        isAllLock: undefined,
                      };
                    }
                    return row;
                  }),
                );
                setFilteredData((old) =>
                  old?.map((row, index) => {
                    if (index === rowIndex) {
                      return {
                        ...old[rowIndex],
                        [columnId]: JSON.parse(value),
                        isAllLock: undefined,
                      };
                    }
                    return row;
                  }),
                );
              },
            }}
            enableFilters
          />
        </div>
      </div>
    </>
  );
};

export default ActivePromotions;
